// packages/charts/src/react/EChart.tsx
//
// Low-level ECharts wrapper. 1:1 port of
// apps/frontend/src/components/charts/EChart.tsx — same
// renderer, same rAF-deferred init, same ResizeObserver, same dispose on
// unmount. Adapted for the sandbox context (no Mantine theming, no reveal
// animation — Phase 4 reveals land at the section boundary, not per chart).
//
// All higher-level components in this package (LineChart, BarChart, …)
// render through this wrapper. Authored JSX does NOT import <EChart/>
// directly; the AST validator does not whitelist it. The per-type
// components are the public surface.
import * as React from "react";
import { echarts } from "./echartsRegistry";
import type { EChartsOption } from "echarts";

export interface EChartProps {
  /** ECharts option object. Passed verbatim to `setOption`. */
  option: EChartsOption;
  /** Optional inline style for the sizing container. */
  style?: React.CSSProperties;
  /**
   * Accessibility label for the chart-as-a-whole. Rendered as
   * `<div role="img" aria-label={ariaLabel}>…`. Complemented by ECharts'
   * own `aria` option (screen-reader content INSIDE the chart).
   */
  ariaLabel?: string;
}

/**
 * Mount an ECharts instance against a div, size it to the parent, and keep
 * it up to date with the supplied option object. Safe against:
 *   - Zero-size parents (accordion panels, tabs) — init is deferred until
 *     the ResizeObserver sees a non-zero rect.
 *   - Strict-mode double-effect in React — disposes the instance on
 *     unmount before the next effect invocation re-initialises.
 *   - Malformed option objects — swallows `setOption` errors so the iframe
 *     doesn't tear on a single bad chart; the empty canvas is a clearer
 *     failure mode than a crashed section.
 */
export function EChart({ option, style, ariaLabel }: EChartProps): React.ReactElement {
  const elRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<echarts.ECharts | null>(null);
  const roRef = React.useRef<ResizeObserver | null>(null);
  const rafRef = React.useRef<number>(0);
  const optionRef = React.useRef<EChartsOption>(option);

  // Keep the option ref updated so deferred init reads the latest.
  optionRef.current = option;

  React.useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const scheduleInitOrResize = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const { clientWidth, clientHeight } = el;
        if (clientWidth === 0 || clientHeight === 0) return;

        if (!chartRef.current || chartRef.current.isDisposed()) {
          // Canvas in the app, SVG in an email. A <canvas> holds pixel
          // state, not markup: capture the DOM and the drawing is gone, so a
          // chart in a report email rendered as an empty box — measured
          // 2026-08-27, and it had been that way since charts existed. SVG
          // survives the capture because it IS markup.
          //
          // The email runtime sets the flag before mounting (see
          // apps/artifact-sandbox/src/email/entry.tsx). Default stays canvas:
          // the app wants the faster renderer, and pre-resolved colours (see
          // the `var(--…)` notes in the chart components) work in both.
          const renderer =
            (globalThis as { __verityChartRenderer?: string }).__verityChartRenderer === "svg"
              ? "svg"
              : "canvas";
          const chart = echarts.init(el, undefined, { renderer });
          chartRef.current = chart;
          try {
            chart.setOption(optionRef.current, { notMerge: true, lazyUpdate: true });
          } catch {
            // Malformed option — ignore rather than crash the section.
          }
        } else {
          try {
            chartRef.current.resize();
          } catch {
            // ignore
          }
        }
      });
    };

    scheduleInitOrResize();

    const ro = new ResizeObserver(() => scheduleInitOrResize());
    ro.observe(el);
    roRef.current = ro;

    return () => {
      cancelAnimationFrame(rafRef.current);
      try {
        roRef.current?.disconnect();
      } catch {
        // ignore
      } finally {
        roRef.current = null;
      }
      try {
        if (chartRef.current && !chartRef.current.isDisposed()) {
          chartRef.current.dispose();
        }
      } catch {
        // ignore
      } finally {
        chartRef.current = null;
      }
    };
  }, []);

  // Update option when it changes post-mount.
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chart.isDisposed()) return;
    try {
      chart.setOption(option, { notMerge: true, lazyUpdate: true });
    } catch {
      // ignore
    }
  }, [option]);

  return (
    <div
      ref={elRef}
      role="img"
      aria-label={ariaLabel ?? "Chart"}
      style={{
        width: "100%",
        height: "100%",
        ...style,
      }}
    />
  );
}
